import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { Box, makeStyles } from '@material-ui/core';
import { foldedLeftNavWidth, leftNavWidth, MDSNavItem } from '@marqvision/mds/core/LeftNavBar';
import { MDSLeftNavBar, MDSTheme } from '@marqvision/mds/core';
import { IcoDocument, IcoEdit, IcoFolderOutline } from '@marqvision/mds/assets';
import { useAuth } from '~/apis/auth';
import { useGetTmNew } from '~/apis/trademarks';
import { Logger } from '~/utils';
import Header, { HeaderHeight } from '../components/Header';
import useBreakpoint from '../hooks/useBreakpoint';
import useRouteNames from '../hooks/useRouteNames';
import FolioLogo from '../components/FolioLogo';
import useLanguage from '../hooks/useLanguage';
import useGlobalAction from '../hooks/useGlobalAction';

interface IMainContext {
  isLNBFixed: boolean;
  setIsLNBFixed: (value: boolean) => void;
  isExistRightPanel: boolean;
  setRenderRightPanel: (node: React.ReactNode, width?: string | number) => void;
}

export const MainContext = createContext<IMainContext>({
  isLNBFixed: true,
  setIsLNBFixed: () => undefined,
  isExistRightPanel: false,
  setRenderRightPanel: () => undefined,
});

const useStyles = makeStyles(() => ({
  bodyWrap: {
    flex: 'auto',
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    height: '100vh',
  },
  contentsWrap: {
    backgroundColor: MDSTheme.palette.bluegray[50],
    flex: 1,
    '&.desktop': {
      overflow: 'auto',
      borderRadius: '24px 24px 0 0',
    },
    '&.isIP': {
      backgroundColor: MDSTheme.palette.white,
    },
    '&:not(.mobile).isSplit': {
      width: '100%',
      display: 'grid',
      backgroundColor: 'transparent',
      gridGap: '8px',
      '&.desktop': {
        borderRadius: 0,
      },
      '&.leftPanel': {},
      '& > div': {
        borderRadius: '24px 24px 0 0',
        backgroundColor: MDSTheme.palette.white,
        overflow: 'hidden',
      },
    },
    minHeight: `calc(100vh - ${HeaderHeight})`,
  },
  rightPanel: {
    '&.mobile': {
      position: 'fixed',
      top: HeaderHeight,
      right: 0,
      bottom: 0,
      left: 0,
      backgroundColor: MDSTheme.palette.white,
      zIndex: 99,
    },
    '&.tablet:not(.mobile)': {
      top: HeaderHeight,
      right: 0,
      bottom: 0,
      // width: 'calc(50% - 2px)',
      backgroundColor: MDSTheme.palette.white,
      zIndex: 3,
    },
  },
}));

const MainLayout = (): JSX.Element => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const { FileAnApplication, MyApplication, TrademarkManagement, Watch, IP, GlobalStatus, FileThankYou } =
    useRouteNames();

  const { data: authData } = useAuth();

  const isFileApplication = pathname.indexOf(FileAnApplication) === 0;
  const isMyApplication = pathname.indexOf(MyApplication) === 0;
  const isWatch = pathname.indexOf(Watch) === 0;
  const isTrademarks = pathname.indexOf(TrademarkManagement) === 0;
  const isGlobalStatus = pathname.indexOf(GlobalStatus) === 0;
  const isIP = pathname.indexOf(IP) === 0;
  const isFileTy = pathname.indexOf(FileThankYou) === 0;

  const { desktop, mobile, tablet, getMediaQueryValue: mq } = useBreakpoint();
  const navigate = useNavigate();
  const RouteNames = useRouteNames();

  const { login } = useGlobalAction();
  const { Lang } = useLanguage();

  const { data } = useGetTmNew();

  const title = (() => {
    if (isFileApplication) {
      return Lang.FileAnApplication;
    }
    if (isMyApplication) {
      return Lang.MyApplication;
    }
    if (isWatch) {
      return Lang.Watch;
    }
    if (isTrademarks) {
      return Lang.trademarks.Trademarks;
    }
    if (isGlobalStatus) {
      return Lang.trademarks.GlobalIpStatus;
    }
    return undefined;
  })();

  const [isLNBFixed, setIsLNBFixed] = useState(true);
  const [rightPanel, setRightPanel] = useState<React.ReactNode>();
  const [rightPanelWidth, setRightPanelWidth] = useState<number | string>();

  const handleSetRightPanel = useCallback((element: React.ReactNode, width?: number | string) => {
    setRightPanel(element);
    setRightPanelWidth(width);
  }, []);

  const leftWidth = isLNBFixed ? leftNavWidth : foldedLeftNavWidth;

  const renderRightPanel = () => {
    return (
      rightPanel && (
        <div
          className={clsx(classes.rightPanel, {
            mobile,
            tablet,
          })}
        >
          {rightPanel}
        </div>
      )
    );
  };

  useEffect(() => {
    window.document.body.style.backgroundColor = MDSTheme.palette.bluegray[850];
    return () => {
      window.document.body.style.backgroundColor = 'white';
    };
  }, []);

  return (
    <MainContext.Provider
      value={{
        isLNBFixed,
        setIsLNBFixed,
        setRenderRightPanel: handleSetRightPanel,
        isExistRightPanel: !!rightPanel,
      }}
    >
      <Box
        display={mq('', '', 'grid')}
        gridTemplateColumns={mq('', '', `${leftWidth}px calc(100% - ${leftWidth}px - 16px)`)}
        gridColumnGap="16px"
      >
        {desktop && (
          <MDSLeftNavBar
            style={{ zIndex: 100 }}
            logo={<FolioLogo color={MDSTheme.palette.white} />}
            HomeLink={RouteNames.Home}
            onOpenNav={setIsLNBFixed}
          >
            <MDSNavItem
              label={Lang.FileAnApplication}
              link={RouteNames.FileAnApplication}
              icon={<IcoEdit />}
              onClick={() => {
                Logger.track('clicked file an application', {
                  Through: 'LNB-File an application',
                });
              }}
            />
            <MDSNavItem
              label={Lang.MyApplication}
              link={RouteNames.MyApplication}
              icon={<IcoDocument />}
              onClick={(e) => {
                Logger.track('clicked my application');
                if (!authData?.accessToken) {
                  e.preventDefault();
                  login(() => navigate(RouteNames.MyApplication));
                }
              }}
            />
            <MDSNavItem
              label={Lang.IpManagement}
              icon={<IcoFolderOutline />}
              subNavItem={[
                {
                  label: Lang.trademarks.GlobalIpStatus,
                  link: RouteNames.GlobalStatus,
                  onClick: (e) => {
                    Logger.track('clicked global ip status');
                    if (!authData?.accessToken) {
                      e.preventDefault();
                      login(() => navigate(RouteNames.GlobalStatus));
                    }
                  },
                },
                {
                  label: Lang.Watch,
                  link: RouteNames.Watch,
                  onClick: () => {
                    Logger.track('clicked watch');
                  },
                },
                {
                  label: Lang.trademarks.Trademarks,
                  link: RouteNames.TrademarkManagement,
                  onClick: () => {
                    Logger.track('clicked trademarks');
                  },
                  tag: data?.hasNewIpTmRegistration
                    ? {
                        label: 'New',
                        color: MDSTheme.palette.red[600],
                      }
                    : undefined,
                },
              ]}
            />
          </MDSLeftNavBar>
        )}
        <Box component="main" className={desktop ? classes.bodyWrap : undefined}>
          <Header
            variant={desktop ? 'dark' : 'normal'}
            title={desktop ? title : undefined}
            shadow
            notification
            hideLoginOrProfile={!!authData?.accessToken && mobile}
            hideChangeLanguage={mq(true, false, false)}
          />
          <div
            id="scrollLayout"
            className={clsx(classes.contentsWrap, {
              desktop,
              mobile,
              isIP,
              isSplit: !!rightPanel,
            })}
            style={{
              gridTemplateColumns: `auto ${rightPanelWidth}`,
              backgroundColor: isFileApplication || isFileTy ? 'white' : undefined,
            }}
          >
            <div className="leftPanel" style={{ height: !rightPanel ? '100%' : undefined }}>
              <Outlet />
            </div>
            {renderRightPanel()}
          </div>
        </Box>
      </Box>
    </MainContext.Provider>
  );
};

export default MainLayout;
